import { useEffect, useRef, type RefObject } from 'react';

/**
 * Scrolls to a section when `shouldScroll` becomes true.
 * Eliminates the duplicated useEffect + setTimeout + scrollIntoView pattern.
 * Uses requestAnimationFrame to ensure the DOM is painted before scrolling.
 */
export function useAutoScroll<T extends HTMLElement>(
    shouldScroll: boolean,
    deps: unknown[] = [],
): RefObject<T | null> {
    const ref = useRef<T | null>(null);
    const hasScrolled = useRef(false);
    const serializedDeps = JSON.stringify(deps);

    useEffect(() => {
        if (!shouldScroll) {
            hasScrolled.current = false;
            return;
        }

        if (hasScrolled.current) return;

        let timeoutId: ReturnType<typeof setTimeout>;

        const rafId = requestAnimationFrame(() => {
            timeoutId = setTimeout(() => {
                if (ref.current) {
                    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    hasScrolled.current = true;
                }
            }, 150);
        });

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(timeoutId);
        };
    }, [shouldScroll, serializedDeps]);

    return ref;
}
